import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Container, Row, Col, Button } from 'react-bootstrap'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })
    
    // Log error to monitoring service in production
    if (import.meta.env.PROD) {
      // TODO: Integrate with error monitoring service (Sentry, LogRocket, etc.)
      console.error('Error caught by boundary:', error, errorInfo)
    }
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="section bg-gray" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
          <Container>
            <Row className="justify-content-center">
              <Col md={8} lg={6} className="text-center">
                <h1 className="text-primary mb-4">Oops! Something went wrong</h1>
                <p className="mb-4">
                  We apologize for the inconvenience. An unexpected error has occurred.
                </p>
                {import.meta.env.DEV && this.state.error && (
                  <details className="mb-4 text-start" style={{ backgroundColor: '#1a1a1a', padding: '1rem', borderRadius: '4px' }}>
                    <summary style={{ cursor: 'pointer', color: '#2eb8ee' }}>
                      Error Details (Development Only)
                    </summary>
                    <pre style={{ color: '#ff6b6b', fontSize: '0.875rem', marginTop: '1rem', whiteSpace: 'pre-wrap' }}>
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </details>
                )}
                <div className="d-flex gap-3 justify-content-center">
                  <Button variant="primary" onClick={this.handleReset}>
                    Try Again
                  </Button>
                  <Button variant="secondary" onClick={this.handleReload}>
                    Reload Page
                  </Button>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary


